/**
 * Tools API Service
 *
 * Centralized API calls for tracking tools
 * Connects to Cloud Functions with Firebase Auth
 */

import { getAuth } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { refusalText } from "../utils/redirectsRequests";

// Base URL - Use relative /api path which:
// - In development: Gets proxied to Cloud Functions by setupProxy.js
// - In production: Gets rewritten to Cloud Functions by firebase.json
const API_BASE = "/api";

/**
 * Get Firebase Auth token
 */
async function getAuthToken() {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    return await user.getIdToken();
  }

  return null;
}

/**
 * Safely parse JSON from response, with helpful error messages
 */
async function parseJsonResponse(response, url) {
  const contentType = response.headers.get("content-type");
  
  // Check if response is JSON
  if (!contentType || !contentType.includes("application/json")) {
    const text = await response.text();
    console.error(`[API] Non-JSON response from ${url}:`, {
      status: response.status,
      contentType,
      bodyPreview: text.substring(0, 200)
    });
    throw new Error(
      `Expected JSON response but got ${contentType || 'unknown content-type'}. ` +
      `This usually means the API endpoint doesn't exist or returned an error page.`
    );
  }

  try {
    return await response.json();
  } catch (error) {
    const text = await response.text();
    console.error(`[API] Invalid JSON from ${url}:`, text.substring(0, 200));
    throw new Error(
      `Invalid JSON response: ${error.message}. ` +
      `Body preview: ${text.substring(0, 100)}`
    );
  }
}

/**
 * Fetch wrapper with error handling and authentication
 */
async function fetchApi(url, options = {}) {
  try {
    // Get auth token
    const token = await getAuthToken();

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      // Try to get error details from response
      let errorDetails = response.statusText;
      try {
        const errorData = await parseJsonResponse(response, url);
        errorDetails =
          errorData.error || errorData.message || JSON.stringify(errorData);
      } catch (e) {
        // Response wasn't JSON, use text
        try {
          const text = await response.text();
          errorDetails = text.substring(0, 200);
        } catch (textError) {
          // Couldn't read body
        }
      }
      throw new Error(`HTTP ${response.status}: ${errorDetails}`);
    }

    const data = await parseJsonResponse(response, url);

    if (!data.success) {
      throw new Error(data.error || "API request failed");
    }

    return data;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
}

/**
 * Resource Orders API
 */
export const ordersApi = {
  /**
   * Get all resource orders
   * @param {Object} filters - Optional filters (limit, status, userId, offset)
   * @returns {Promise<{success: boolean, orders: Array, count: number, pagination: Object}>}
   */
  getOrders: async (filters = {}) => {
    const params = new URLSearchParams({
      limit: filters.limit || 100,
      offset: filters.offset || 0,
      ...(filters.status && { status: filters.status }),
      ...(filters.userId && { userId: filters.userId }),
    });
    return fetchApi(`${API_BASE}/promoterResourceOrders?${params}`);
  },

  /**
   * Get detailed order information
   * @param {string} orderId - The order ID
   * @returns {Promise<{success: boolean, order: Object}>}
   */
  getOrderDetail: async (orderId) => {
    return fetchApi(`${API_BASE}/promoterResourceOrderDetail?orderId=${orderId}`);
  },

  /**
   * Update order status
   * @param {string} orderId - The order ID
   * @param {string} status - New status (pending, processing, shipped, delivered, cancelled)
   * @returns {Promise<{success: boolean, message: string, previousStatus: string, newStatus: string}>}
   */
  updateOrderStatus: async (orderId, status) => {
    // Use HTTP endpoint approach with auth token (like other protected functions)
    const body = JSON.stringify({ orderId, status });

    return fetchApi(`${API_BASE}/promoterResourceOrderStatus`, {
      method: "POST",
      body,
    });
  },
};

// companyApi (basicCompanyInfo, one public request per key) — removed 25 Sep 2026.
// Company names come from services/companyCache.js → the member-only lookupCompanyNames.
/**
 * Banners API
 */
export const bannersApi = {
  /**
   * Get all banner statistics
   * @param {Object} filters - Optional filters (referralKey, design, style, size)
   * @returns {Promise<{success: boolean, totalImageViews: number, totalClicks: number, banners: Array}>}
   */
  getAllStats: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const url = params.toString()
      ? `${API_BASE}/bannerStats?${params}`
      : `${API_BASE}/bannerStats`;

    // bannerStats doesn't use standard {success: true} format, so custom fetch
    try {
      const token = await getAuthToken();
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await parseJsonResponse(response, url);
    } catch (error) {
      console.error("Banner Stats API Error:", error);
      throw error;
    }
  },

  /**
   * Get banner statistics for a specific company
   * @param {string} referralKey - Company referral key
   * @returns {Promise<{success: boolean, banners: Array}>}
   */
  getCompanyBanners: async (referralKey) => {
    return bannersApi.getAllStats({ referralKey });
  },

  /**
   * Get time-series data for charts
   * @param {string} referralKey - Company referral key
   * @param {string} timeRange - Time range (1day, 3days, 1week, 2weeks, 1month)
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  getTimeseries: async (referralKey, timeRange = "1week") => {
    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE}/bannerTimeseries?referralKey=${referralKey}&timeRange=${timeRange}`,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Banner Timeseries API Error:", error);
      throw error;
    }
  },
};

/**
 * Promoter Visits API
 */
export const promotersApi = {
  getStats: async () => {
    return fetchApi(`${API_BASE}/promoterStats`);
  },

  getTimeseries: async (referralKey, range = "30d") => {
    return fetchApi(
      `${API_BASE}/promoterTimeseries?referralKey=${referralKey}&range=${range}`
    );
  },

  getVisits: async (referralKey) => {
    return fetchApi(`${API_BASE}/promoterVisits?referralKey=${referralKey}`);
  },
};

/**
 * Promoter Visits API
 */
export const promoterVisitsApi = {
  /**
   * Get visits for a specific company, optionally grouped by session
   * @param {string} referralKey - Company referral key
   * @param {string} groupBy - Optional grouping: 'session' or undefined
   * @returns {Promise<{success: boolean, visits: Array} | {success: boolean, sessions: Array}>}
   */
  getCompanyVisits: async (referralKey, groupBy = null) => {
    const params = new URLSearchParams({ referralKey });
    if (groupBy) {
      params.append("groupBy", groupBy);
    }
    return fetchApi(`${API_BASE}/promoterVisits?${params}`);
  },
};

/**
 * Redirects API (QR/Social tracking)
 */
export const redirectsApi = {
  /**
   * Get all redirect campaigns with stats
   * @param {Object} filters - Optional filters
   * @param {string} filters.country - Country code (e.g., 'GB') or 'global'
   * @param {string} filters.trafficType - 'all', 'human', or 'bot'
   * @returns {Promise<{totalScansToday: number, campaigns: Array}>}
   */
  getAllStats: async (filters = {}) => {
    // qrStats doesn't use standard {success: true} format
    const params = new URLSearchParams();
    if (filters.country && filters.country !== "global") {
      params.append("country", filters.country);
    }
    if (filters.trafficType && filters.trafficType !== "all") {
      params.append("trafficType", filters.trafficType);
    }

    const url = `${API_BASE}/qrStats${params.toString() ? `?${params}` : ""}`;

    try {
      const token = await getAuthToken();
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        // The server's own words when it refuses (too many scans, a bad filter, not a
        // colleague), not just the status: the page shows this message.
        throw new Error(await refusalText(response));
      }

      return await parseJsonResponse(response, url);
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  },

  /**
   * Get the FULL daily time-series for a campaign (clozesuretools-owned
   * qrCampaignTimeseries). Returns per-day total + scansByType + scansByCountry,
   * so the caller can derive the chart line (all / per-tag / per-country) and
   * the windowed source & country breakdowns from one response.
   *
   * @param {string} campaignId - Campaign ID
   * @param {number} days - Number of days to return (e.g. 7, 30, 90)
   * @returns {Promise<{campaignId: string, timeRange: string, data: Array<{date: string, scans: number, scansByType: Object, scansByCountry: Object}>}>}
   */
  getTimeseries: async (campaignId, days = 7) => {
    const params = new URLSearchParams({ campaignId, days: String(days) });
    const url = `${API_BASE}/qrCampaignTimeseries?${params}`;
    try {
      const token = await getAuthToken();
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await parseJsonResponse(response, url);
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  },

  /**
   * Get daily time-series for ALL campaigns in one request (read-only).
   *
   * Backs the per-row sparklines on the Redirects dashboard without an N+1
   * read storm. Honours the Country and Human filters (human uses the per-day
   * human breakdown fields).
   *
   * @param {number} days - window size (default 30, clamped 1..90 server-side)
   * @param {string} country - Optional country code (e.g. "GB")
   * @param {boolean} human - Optional; sums the human-only per-day fields
   * @returns {Promise<{days: number, dates: string[], series: Object<string, number[]>}>}
   */
  getAllTimeseries: async (days = 30, country = null, human = false) => {
    const params = new URLSearchParams({ days: String(days) });
    if (country && country !== "global") {
      params.append("country", country);
    }
    if (human) {
      params.append("human", "true");
    }
    const url = `${API_BASE}/qrBatchTimeseries?${params}`;
    try {
      const token = await getAuthToken();
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await parseJsonResponse(response, url);
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  },

  /**
   * Create or update a redirect alias
   * @param {string} alias - The alias (3-50 chars, lowercase alphanumeric + hyphens)
   * @param {string} campaignId - The campaign ID
   * @param {string} campaignType - The campaign type (e.g., "booklet", "flyer", or "default")
   * @returns {Promise<{success: boolean, message: string}>}
   */
  createAlias: async (alias, campaignId, campaignType = "default") => {
    const url = `${API_BASE}/aliasCreate`;
    const data = { alias, campaignId, campaignType };
    return await fetchApi(url, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a redirect alias.
   * NOTE: the deployed aliasDelete handler requires HTTP DELETE (not POST).
   * @param {string} alias - The alias to delete
   * @returns {Promise<{success: boolean, message: string}>}
   */
  deleteAlias: async (alias) => {
    const url = `${API_BASE}/aliasDelete`;
    const data = { alias };
    return await fetchApi(url, {
      method: "DELETE",
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a campaign's UTM parameters (medium / content). Passing null/empty
   * for utmMedium clears the override (reverts to auto-calculated).
   * @param {string} campaignId
   * @param {{utmMedium?: string|null, utmContent?: string|null}} fields
   * @returns {Promise<{success: boolean, campaignId: string, updated: Object}>}
   */
  updateCampaignUtm: async (campaignId, { utmMedium, utmContent } = {}) => {
    const url = `${API_BASE}/updateCampaignUtm`;
    const data = { campaignId };
    if (utmMedium !== undefined) data.utmMedium = utmMedium;
    if (utmContent !== undefined) data.utmContent = utmContent;
    return await fetchApi(url, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Register a campaign (+ optional source) so it appears in the dashboard
   * immediately with zero stats, rather than waiting for the first visit.
   * Idempotent + non-destructive server-side (never resets existing counters).
   * Backed by /api/registerCampaign → registerCampaign (admin-gated).
   *
   * @param {Object} fields - { campaignId, campaignType?, displayName?, utmMedium?, originalUrl? }
   * @returns {Promise<{success: boolean, created: boolean, campaignId: string, message: string}>}
   */
  registerCampaign: async (fields = {}) => {
    const url = `${API_BASE}/registerCampaign`;
    return await fetchApi(url, {
      method: "POST",
      body: JSON.stringify(fields),
    });
  },

  /**
   * Reset / delete campaign stats (admin).
   *
   * Backed by /api/resetStats → resetStats. Reconciles the campaign doc AND
   * qr_daily_stats (and deletes the matching qr_scans), so every dashboard
   * panel stays consistent.
   *
   * @param {string} campaignId
   * @param {'today'|'all'|'delete'|'delete-tag'} resetType
   *   - today: subtract & delete today's daily-stats doc
   *   - all: zero the campaign (kept visible) + delete all daily stats & scans
   *   - delete: remove the campaign entirely
   *   - delete-tag: remove a single source/tag (requires tagType)
   * @param {string|null} tagType - the source/tag, required for 'delete-tag'
   * @returns {Promise<{success: boolean, message: string, resetType: string}>}
   */
  resetStats: async (campaignId, resetType, tagType = null) => {
    const url = `${API_BASE}/resetStats`;
    const data = { campaignId, resetType };
    if (tagType) data.tagType = tagType;
    return await fetchApi(url, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Adjust a single source's scans (admin).
   *
   * The dashboard only uses DATE-BASED deletion, which reconciles the campaign
   * doc AND qr_daily_stats. (The function also supports an integer `decreaseBy`
   * mode, but that updates the campaign doc only and would desync the
   * daily-stats-driven charts — deliberately not surfaced in the UI.)
   *
   * NOTE: qrCountDecrease returns `{ message, operation, ... }` with NO
   * `success` field, so this uses a custom fetch rather than fetchApi.
   *
   * @param {string} campaignId
   * @param {string} campaignType - the source/tag (e.g. "booklet", "default")
   * @param {Object} opts - exactly one mode:
   *   {deleteAfterDate} | {deleteBeforeDate} | {deleteFromDate, deleteToDate} | {decreaseBy}
   *   (dates are ISO strings, e.g. "2026-06-01T00:00:00.000Z")
   * @returns {Promise<{message: string, deletedScansCount?: number, operation: string}>}
   */
  decreaseCount: async (campaignId, campaignType, opts = {}) => {
    const url = `${API_BASE}/qrCountDecrease`;
    const body = JSON.stringify({ campaignId, campaignType, ...opts });
    try {
      const token = await getAuthToken();
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body,
      });

      if (!response.ok) {
        // qrCountDecrease returns { error } on failure
        let detail = response.statusText;
        try {
          const errData = await parseJsonResponse(response, url);
          detail = errData.error || errData.message || detail;
        } catch (e) {
          /* non-JSON error body */
        }
        throw new Error(`HTTP ${response.status}: ${detail}`);
      }

      return await parseJsonResponse(response, url);
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  },

  /**
   * Get summary data for dashboard widget
   * @returns {Promise<{success: boolean, summary: Object}>}
   */
  getSummary: async () => {
    const url = `${API_BASE}/redirectsSummary`;
    try {
      const token = await getAuthToken();
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await parseJsonResponse(response, url);
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  },

  /**
   * Get all redirect aliases.
   *
   * Uses /api/aliasGet (the LIVE `campaign_aliases` collection that the `go`
   * redirect actually resolves), not /api/aliasesGetAll (which reads the empty
   * orphaned `qr_aliases` collection). aliasGet returns a flat array; we
   * reshape it into { [campaignId]: { [source]: { alias, path } } }.
   *
   * NOTE on landing paths: the `go` resolver builds `/hs/{campaignId}/{campaignType}`,
   * so an optional landing path rides INSIDE campaignType as "source/path"
   * (e.g. "conveyancers/conveyancers" → clozesure.com/conveyancers). We split on
   * the first "/": the part before is the SOURCE we key by (so it still matches
   * the campaign's scan-derived source rows), the rest is the landing PATH
   * ("" = homepage). campaignType "default" = campaign-level alias.
   *
   * @returns {Promise<{success: boolean, aliases: Object<string, Object<string, {alias: string, path: string}>>}>}
   */
  getAllAliases: async () => {
    const url = `${API_BASE}/aliasGet`;
    try {
      const token = await getAuthToken();
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await parseJsonResponse(response, url);

      // Reshape flat array → { [campaignId]: { [source]: { alias, path } } }.
      // campaignType may be "source/landing/path" — split off the source (key)
      // from the landing path (everything after the first "/").
      const aliases = {};
      (data.aliases || []).forEach(({ alias, campaignId, campaignType }) => {
        if (!campaignId) return;
        const raw = campaignType || "default";
        const slash = raw.indexOf("/");
        const source = slash === -1 ? raw : raw.slice(0, slash);
        const path = slash === -1 ? "" : raw.slice(slash + 1);
        if (!aliases[campaignId]) aliases[campaignId] = {};
        aliases[campaignId][source || "default"] = { alias, path };
      });

      return { success: true, aliases };
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  },
};

/**
 * Dashboard Summary API
 */
export const dashboardApi = {
  getResourceOrdersSummary: async () => {
    return fetchApi(`${API_BASE}/getResourceOrdersSummary`);
  },
  getPromoterVisitsSummary: async () => {
    // Served by promoterStats (reads the pre-aggregated promoter_*_stats
    // collections). Returns { success, summary: { today, week, companies }, ... }.
    return fetchApi(`${API_BASE}/promoterStats`);
  },
  getBannerAdsSummary: async () => {
    return fetchApi(`${API_BASE}/bannerAdsSummary`);
  },
  getRedirectsSummary: async () => {
    return fetchApi(`${API_BASE}/redirectsSummary`);
  },
  getMapSummary: async () => {
    // The map tile's four numbers, counted on the server when the map data is built
    // (functions/map/summary.js). Colleagues only. It replaced two downloads, of every
    // promoter and every deal, that this tile made to count them in the browser.
    const result = await httpsCallable(functions, "getMapSummary")();
    return result.data;
  },

};

export default {
  orders: ordersApi,
  redirects: redirectsApi,
  banners: bannersApi,
  promoters: promotersApi,
  promoterVisits: promoterVisitsApi,
  dashboard: dashboardApi,
};
